import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import classList from 'flarum/common/utils/classList';
import extractText from 'flarum/common/utils/extractText';
import SpeciesGbifSearchField from './SpeciesGbifSearchField';
import SpeciesCorrectionModal from './SpeciesCorrectionModal';
import { promptLogin, loginRequiredPlaceholder, permissionDeniedPlaceholder } from '../utils/requireAccess';
import { fieldValue, SYNONYM_TYPE, TABLES } from '../utils/speciesCorrectionTables';
import gbifBackboneUrl from '../../common/utils/gbifBackboneUrl';
// Search ignores case and accents ("Agaricus" = "agaricus", "é" = "e").
const normalize = text => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * /species-corrections (champis-species.manage) - the GBIF correction
 * tables, one tab each (see TABLES), laid out like the exhibitions pages:
 * the active table as a section - its colored header with the entry
 * count and "Ajouter", a search, one compact row per entry with its edit/
 * delete icons - and adding or editing in a modal (SpeciesCorrectionModal)
 * rather than a form at the bottom of a hundred rows.
 *
 * Synonyms are the one exception - see SYNONYM_TYPE for why they're
 * managed inline on each Accepted row, as removable chips, instead of
 * getting their own tab. Rows are sorted alphabetically by their
 * `sortField` (client-side - these tables are small, no server sort
 * needed).
 */
export default class SpeciesCorrectionsPage extends Page {
  activeTab = (() => TABLES[0].type)();
  rows = {};
  loading = {};
  query = '';
  requiresLogin = false;
  permissionDenied = false;
  loadingSynonyms = false;
  addingSynonymFor = null;
  synonymGbifId = null;
  synonymScientificName = '';
  synonymError = null;
  oninit(vnode) {
    super.oninit(vnode);
    app.setTitle(extractText(app.translator.trans('champis-species.forum.page.title')));
    this.activeTab = TABLES[0].type;
    this.rows = {};
    this.loading = {};
    this.query = '';
    this.requiresLogin = false;
    this.permissionDenied = false;

    // Every one of this page's own resources (AcceptedSpeciesResource etc.)
    // rejects anyone without champis-species.manage - checked here rather
    // than letting each table's own request fail, so a guest gets a login
    // prompt (they might be a curator not yet signed in) and a logged-in
    // non-curator gets a clear "no permission" message instead of both
    // landing on the same generic per-table error (see requireAccess.js's
    // own docblock).
    if (!app.session.user) {
      this.requiresLogin = true;
      promptLogin();
      return;
    }
    if (!app.forum.attribute('canManageSpecies')) {
      this.permissionDenied = true;
      return;
    }

    // Per-Accepted-row "add a synonym" state - only one row's form is ever
    // open at a time, so this is shared rather than keyed per row.
    this.addingSynonymFor = null;
    this.synonymGbifId = null;
    this.synonymScientificName = '';
    this.synonymError = null;
    TABLES.forEach(table => this.loadTable(table));
    this.loadSynonyms();
  }
  loadTable(table) {
    this.loading[table.type] = true;
    m.redraw();
    app.store.find(table.type).then(results => {
      this.rows[table.type] = [...results].sort((a, b) => fieldValue(a, table.sortField).localeCompare(fieldValue(b, table.sortField), 'fr'));
      this.loading[table.type] = false;
      m.redraw();
    }).catch(() => {
      this.loading[table.type] = false;
      m.redraw();
    });
  }
  loadSynonyms() {
    this.loadingSynonyms = true;
    m.redraw();
    app.store.find(SYNONYM_TYPE, {
      include: 'acceptedSpecies'
    }).then(results => {
      this.synonyms = results;
      this.loadingSynonyms = false;
      m.redraw();
    }).catch(() => {
      this.loadingSynonyms = false;
      m.redraw();
    });
  }
  synonymsFor(acceptedRow) {
    return (this.synonyms || []).filter(s => {
      const accepted = s.acceptedSpecies && s.acceptedSpecies();
      return accepted && accepted.id() === acceptedRow.id();
    });
  }
  view() {
    if (this.requiresLogin || this.permissionDenied) {
      return m("div", {
        className: "Page SpeciesCorrectionsPage"
      }, m("div", {
        className: "container"
      }, this.requiresLogin ? loginRequiredPlaceholder(app.translator.trans('champis-species.forum.page.login_required')) : permissionDeniedPlaceholder(app.translator.trans('champis-species.forum.page.permission_denied'))));
    }
    const table = TABLES.find(t => t.type === this.activeTab);
    return m("div", {
      className: "Page SpeciesCorrectionsPage"
    }, m("div", {
      className: "container"
    }, m("h1", {
      className: "ChampisDiscussionList-header"
    }, m("i", {
      className: "icon fas fa-list-check",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-species.forum.page.title')), m("div", {
      className: "SpeciesCorrectionsPage-intro"
    }, m("div", {
      className: "SpeciesCorrectionsPage-tabs",
      role: "tablist"
    }, TABLES.map(t => {
      const rows = this.rows[t.type];
      return m("button", {
        type: "button",
        role: "tab",
        "aria-selected": this.activeTab === t.type,
        className: classList('SpeciesCorrectionsPage-tab', this.activeTab === t.type && 'SpeciesCorrectionsPage-tab--active'),
        onclick: () => this.selectTab(t)
      }, m("i", {
        className: `icon ${t.icon}`,
        "aria-hidden": "true"
      }), " ", app.translator.trans(t.titleKey), rows && m("span", {
        className: "SpeciesCorrectionsPage-count"
      }, rows.length));
    })), m("p", {
      className: "SpeciesCorrectionsPage-hint"
    }, app.translator.trans(table.hintKey))), this.section(table)));
  }
  section(table) {
    const t = key => app.translator.trans(`champis-species.forum.page.${key}`);
    const rows = this.rows[table.type];
    const visible = (rows || []).filter(row => this.matches(table, row));
    return m("div", {
      className: "SpeciesCorrectionsPage-section"
    }, m("div", {
      className: "SpeciesCorrectionsPage-sectionHeader"
    }, m("h3", null, m("i", {
      className: `icon ${table.icon}`,
      "aria-hidden": "true"
    }), " ", app.translator.trans(table.titleKey), rows && m("span", {
      className: "SpeciesCorrectionsPage-headerCount"
    }, rows.length)), m(Button, {
      className: "Button",
      icon: "fas fa-plus",
      onclick: () => this.openModal(table)
    }, t('add_title'))), m("div", {
      className: "SpeciesCorrectionsPage-sectionBody"
    }, rows && rows.length > 0 && m("input", {
      className: "FormControl SpeciesCorrectionsPage-search",
      type: "search",
      placeholder: extractText(t('search_placeholder')),
      "aria-label": extractText(t('search_placeholder')),
      value: this.query,
      oninput: e => this.query = e.target.value
    }), !rows ? this.loading[table.type] ? m(LoadingIndicator, null) : m(Placeholder, {
      text: t('error')
    }) : rows.length === 0 ? m(Placeholder, {
      text: t('empty')
    }) : visible.length === 0 ? m(Placeholder, {
      text: t('no_results')
    }) : m("ul", {
      className: "SpeciesCorrectionsPage-rows"
    }, visible.map(row => this.row(table, row)))));
  }
  row(table, row) {
    const t = key => app.translator.trans(`champis-species.forum.page.${key}`);
    const value = key => fieldValue(row, key);
    return m("li", {
      key: row.id(),
      className: "SpeciesCorrectionsPage-row"
    }, m("div", {
      className: "SpeciesCorrectionsPage-rowBody"
    }, m("div", {
      className: "SpeciesCorrectionsPage-rowName"
    }, table.type === 'champis-species-new' ? `${value('canonicalName')} ${value('author')}`.trim() : value('scientificName')), m("div", {
      className: "SpeciesCorrectionsPage-rowMeta"
    }, this.meta(table, row)), this.isAccepted(table) && this.synonymsOf(row)), m("div", {
      className: "SpeciesCorrectionsPage-rowActions"
    }, m("button", {
      type: "button",
      "aria-label": extractText(t('edit_button')),
      title: extractText(t('edit_button')),
      onclick: () => this.openModal(table, row)
    }, m("i", {
      className: "icon fas fa-pen",
      "aria-hidden": "true"
    })), m("button", {
      type: "button",
      "aria-label": extractText(t('delete_button')),
      title: extractText(t('delete_button')),
      onclick: () => this.delete(table, row)
    }, m("i", {
      className: "icon fas fa-trash",
      "aria-hidden": "true"
    }))));
  }

  /**
   * The entry's details under its name: its GBIF ids - links to their
   * GBIF Backbone entry (gbifBackboneUrl()), to check what's being
   * corrected - and, depending on the
   * table, its corrected parent or the new species' rank and parent.
   */
  meta(table, row) {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-species.forum.page.${key}`, params);
    };
    const value = key => fieldValue(row, key);
    const gbif = id => m("a", {
      href: gbifBackboneUrl(id),
      target: "_blank",
      rel: "noopener noreferrer"
    }, "GBIF ", id);
    switch (table.type) {
      case 'champis-species-parent':
        return [m("span", null, gbif(value('gbifId'))), m("span", null, t('parent_label'), " ", value('parentScientificName'), " (", gbif(value('parentGbifId')), ")")];
      case 'champis-species-new':
        return [value('rank') && m("span", null, this.rankLabel(value('rank'))), m("span", null, t('parent_label'), " ", value('parentScientificName'), " (", gbif(value('parentGbifId')), ")"),
        // A synthetic id, nothing on GBIF's side: shown, not linked.
        m("span", null, t('local_id', {
          id: value('gbifId')
        }))];
      default:
        return [m("span", null, gbif(value('gbifId')))];
    }
  }

  // In French when known ("VARIETY" -> "Variété"), as stored otherwise.
  rankLabel(rank) {
    const key = `champis-species.forum.page.ranks.${rank}`;
    const label = extractText(app.translator.trans(key));
    return label === key ? rank : label;
  }

  /**
   * An Accepted row's locally-added synonyms, as removable chips, then
   * "+ Synonyme" - which opens a search right there, for that row only.
   */
  synonymsOf(acceptedRow) {
    const t = key => app.translator.trans(`champis-species.forum.page.${key}`);
    const synonyms = this.synonymsFor(acceptedRow);
    return m("div", {
      className: "SpeciesCorrectionsPage-synonyms"
    }, this.loadingSynonyms && !this.synonyms ? m(LoadingIndicator, {
      display: "inline",
      size: "small"
    }) : synonyms.map(s => m("span", {
      className: "SpeciesCorrectionsPage-synonym",
      key: s.id()
    }, s.scientificName(), m("button", {
      type: "button",
      "aria-label": extractText(t('remove_synonym_button')),
      title: extractText(t('remove_synonym_button')),
      onclick: () => this.deleteSynonym(s)
    }, m("i", {
      className: "icon fas fa-times",
      "aria-hidden": "true"
    })))), this.addingSynonymFor !== acceptedRow && m("button", {
      type: "button",
      className: "SpeciesCorrectionsPage-addSynonym",
      onclick: () => this.startAddSynonym(acceptedRow)
    }, m("i", {
      className: "icon fas fa-plus",
      "aria-hidden": "true"
    }), " ", t('add_synonym_button')), this.addingSynonymFor === acceptedRow && this.synonymForm(acceptedRow));
  }
  synonymForm(acceptedRow) {
    const t = key => app.translator.trans(`champis-species.forum.page.${key}`);
    return m("div", {
      className: "SpeciesCorrectionsPage-synonymForm"
    }, m(SpeciesGbifSearchField, {
      initialQuery: "",
      placeholder: app.translator.trans('champis-species.forum.search.placeholder'),
      required: true,
      onselect: species => {
        this.synonymGbifId = species.id;
        this.synonymScientificName = species.scientificName || '';
        m.redraw();
      },
      onclear: () => {
        this.synonymGbifId = null;
      }
    }), m(Button, {
      className: "Button Button--primary",
      onclick: () => this.submitSynonym(acceptedRow)
    }, t('add_button')), m(Button, {
      className: "Button",
      onclick: () => this.cancelAddSynonym()
    }, t('cancel_button')), this.synonymError && m("p", {
      className: "SpeciesCorrectionsPage-error"
    }, this.synonymError));
  }

  /**
   * Its name(s) - the corrected parent's and the synonyms' too - or its
   * GBIF ids contain the search.
   */
  matches(table, row) {
    const query = normalize(this.query.trim());
    if (!query) return true;
    const texts = table.fields.map(field => fieldValue(row, field.key));
    if (this.isAccepted(table)) {
      texts.push(...this.synonymsFor(row).map(s => s.scientificName() || ''));
    }
    return texts.some(text => normalize(text).includes(query));
  }
  selectTab(table) {
    this.activeTab = table.type;
    this.query = '';
    this.addingSynonymFor = null;
  }
  isAccepted(table) {
    return table.type === 'champis-species-accepted';
  }
  openModal(table, row) {
    app.modal.show(SpeciesCorrectionModal, {
      table,
      row,
      onsaved: () => this.loadTable(table)
    });
  }
  startAddSynonym(acceptedRow) {
    this.addingSynonymFor = acceptedRow;
    this.synonymGbifId = null;
    this.synonymScientificName = '';
    this.synonymError = null;
  }
  cancelAddSynonym() {
    this.addingSynonymFor = null;
    this.synonymError = null;
  }
  submitSynonym(acceptedRow) {
    if (!this.synonymGbifId) {
      this.synonymError = app.translator.trans('champis-species.forum.page.pick_species_error');
      m.redraw();
      return;
    }
    this.synonymError = null;
    app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/' + SYNONYM_TYPE,
      body: {
        data: {
          type: SYNONYM_TYPE,
          attributes: {
            gbifId: this.synonymGbifId,
            scientificName: this.synonymScientificName
          },
          relationships: {
            acceptedSpecies: {
              data: {
                type: 'champis-species-accepted',
                id: acceptedRow.id()
              }
            }
          }
        }
      }
    }).then(() => {
      this.addingSynonymFor = null;
      this.loadSynonyms();
    }).catch(error => {
      this.synonymError = error.message || app.translator.trans('champis-species.forum.page.error');
      m.redraw();
    });
  }
  deleteSynonym(synonym) {
    if (!confirm(app.translator.trans('champis-species.forum.page.delete_confirm'))) {
      return;
    }
    app.request({
      method: 'DELETE',
      url: app.forum.attribute('apiUrl') + '/' + SYNONYM_TYPE + '/' + synonym.id()
    }).then(() => this.loadSynonyms());
  }
  delete(table, row) {
    if (!confirm(app.translator.trans('champis-species.forum.page.delete_confirm'))) {
      return;
    }
    app.request({
      method: 'DELETE',
      url: app.forum.attribute('apiUrl') + '/' + table.type + '/' + row.id()
    }).then(() => this.loadTable(table));
  }
}
flarum.reg.add('champis-species', 'forum/components/SpeciesCorrectionsPage', SpeciesCorrectionsPage);